window.theme = window.theme || {};

// Init section function when it's visible, then disable observer
theme.initWhenVisible = function(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, {rootMargin: `0px 0px ${threshold}px 0px`});

  observer.observe(options.element);
};

class ScrollingPromotion extends HTMLElement {
  constructor() {
    super();

    this.config = {
      moveTime: parseFloat(this.dataset.speed) || 1.6, // Default fallback
      space: 100, // 100px
    };

    this.promotion = this.querySelector('.promotion');
    this.isInitialized = false;
    this.resizeTimeout = null;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    if (this.isInitialized) return;
    
    if (this.childElementCount === 1) {
      this.promotion.classList.add('promotion--animated');

      for (let index = 0; index < 10; index++) {
        this.clone = this.promotion.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);

        const imageWrapper = this.clone.querySelector('.media-wrapper');
        if (imageWrapper) {
          imageWrapper.classList.remove('loading');
        }
      }

      // Wait for images and fonts to load
      this.waitForContentLoad().then(() => {
        this.setAnimationDuration();
      });
      
      // Handle resize with debouncing
      this.boundHandleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.boundHandleResize);
      
      // Handle orientation change specifically for mobile
      this.boundOrientationChange = () => {
        setTimeout(() => this.setAnimationDuration(), 300);
      };
      window.addEventListener('orientationchange', this.boundOrientationChange);

      // Pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.scrollingPlay();
          } else {
            this.scrollingPause();
          }
        });
      }, {rootMargin: '0px 0px 50px 0px'});

      observer.observe(this);
      this.isInitialized = true;
    }
  }

  async waitForContentLoad() {
    // Wait for all images to load
    const images = this.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => {
        img.addEventListener('load', resolve);
        img.addEventListener('error', resolve);
        setTimeout(resolve, 3000); // Timeout after 3s
      });
    });

    // Wait for fonts to load
    const fontPromise = document.fonts ? document.fonts.ready : Promise.resolve();

    // Wait for all content
    await Promise.all([...imagePromises, fontPromise]);
    
    // Additional delay for iOS Safari layout
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  setAnimationDuration() {
    // Force multiple reflows for iOS Safari
    void this.offsetWidth;
    void this.promotion.offsetWidth;
    
    const promotionWidth = this.promotion.scrollWidth || this.promotion.clientWidth;
    
    // iOS Safari specific: Get computed width as fallback
    if (!promotionWidth || promotionWidth <= 0) {
      const computedStyle = window.getComputedStyle(this.promotion);
      const computedWidth = parseFloat(computedStyle.width);
      
      if (computedWidth && computedWidth > 0) {
        this.calculateAndSetDuration(computedWidth);
        return;
      }
      
      console.warn('Invalid promotion width, retrying...');
      setTimeout(() => this.setAnimationDuration(), 150);
      return;
    }

    this.calculateAndSetDuration(promotionWidth);
  }

  calculateAndSetDuration(width) {
    // Calculate animation duration with bounds checking
    let animationTimeFrame = (width / this.config.space) * this.config.moveTime;
    
    // iOS Safari: Multiply by 1.2 to compensate for faster rendering
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      animationTimeFrame *= 1.3; // Slow down by 30% on iOS
    }
    
    // Ensure the duration is reasonable (between 8s and 120s)
    const boundedDuration = Math.max(8, Math.min(120, animationTimeFrame));
    
    // Use requestAnimationFrame to ensure smooth updates
    requestAnimationFrame(() => {
      this.style.setProperty('--duration', `${boundedDuration}s`);
    });
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setAnimationDuration();
    }, 150);
  }

  scrollingPlay() {
    this.classList.remove('scrolling-promotion--paused');
  }

  scrollingPause() {
    this.classList.add('scrolling-promotion--paused');
  }
  
  disconnectedCallback() {
    // Clean up event listeners
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
    if (this.boundOrientationChange) {
      window.removeEventListener('orientationchange', this.boundOrientationChange);
    }
    clearTimeout(this.resizeTimeout);
  }
}

customElements.define('scrolling-promotion', ScrollingPromotion);